import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { openApi } from "../../services/api";
import "./styles.css";

const statusLabel = {
  idle: "checkout nao criado",
  loading: "carregando",
  creating: "checkout criando",
  ready: "checkout pronto",
  pending: "checkout pronto",
  expired: "checkout expirado",
  failed: "checkout falhou",
  cancelled: "checkout cancelado",
  disabled: "indisponivel"
};

const randomIdempotencyKey = () =>
  `checkout-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const BillingCheckoutPendingPage = () => {
  const { onboardingId } = useParams();
  const statusTokenKey = `ticketwhats_status_token_${onboardingId}`;
  const statusToken = sessionStorage.getItem(statusTokenKey) || "";
  const [featureState, setFeatureState] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("PIX");
  const [checkout, setCheckout] = useState(null);
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState("");

  const methods = useMemo(() => {
    if (!featureState || !Array.isArray(featureState.allowedMethods)) return [];
    return featureState.allowedMethods.filter(method => method === "PIX" || method === "BOLETO");
  }, [featureState]);

  useEffect(() => {
    if (!statusToken) {
      setStatus("failed");
      setError("Sessão de checkout inválida ou expirada. Selecione um plano para tentar novamente.");
      return;
    }

    let mounted = true;
    openApi
      .get("/billing/checkout/feature-state")
      .then(({ data }) => {
        if (!mounted) return;
        setFeatureState(data);
        setStatus(data.checkoutEnabled ? "idle" : "disabled");
        if (Array.isArray(data.allowedMethods) && data.allowedMethods[0]) {
          setPaymentMethod(data.allowedMethods[0]);
        }
      })
      .catch(() => {
        if (!mounted) return;
        setStatus("failed");
        setError("Não foi possível carregar o checkout.");
      });
    return () => {
      mounted = false;
    };
  }, [statusToken]);

  const createCheckout = async () => {
    if (!statusToken) return;
    setStatus("creating");
    setError("");
    try {
      const { data } = await openApi.post(
        `/public/billing/signup/${onboardingId}/checkout`,
        { paymentMethod },
        {
          headers: {
            "x-status-token": statusToken,
            "idempotency-key": randomIdempotencyKey()
          }
        }
      );
      setCheckout(data);
      setStatus(data.status);
    } catch (err) {
      setStatus("failed");
      setError(err?.response?.data?.error || err?.response?.data?.message || "Checkout indisponível.");
    }
  };

  const cancelCheckout = async () => {
    if (!checkout?.checkoutId || !statusToken) return;
    setError("");
    try {
      const { data } = await openApi.post(
        `/public/billing/signup/${onboardingId}/checkout/${checkout.checkoutId}/cancel`,
        {},
        {
          headers: {
            "x-status-token": statusToken,
            "idempotency-key": randomIdempotencyKey()
          }
        }
      );
      setCheckout(data);
      setStatus(data.status);
      sessionStorage.removeItem(statusTokenKey);
    } catch (err) {
      setError(err?.response?.data?.error || err?.response?.data?.message || "Cancelamento indisponível.");
    }
  };

  const copyPix = async () => {
    if (!checkout?.pix?.copyPaste) return;
    await navigator.clipboard.writeText(checkout.pix.copyPaste);
  };

  return (
    <main className="billingCheckoutPage">
      <section className="billingCheckoutPanel">
        <div>
          <p className="billingCheckoutKicker">Billing V2 sandbox</p>
          <h1>Checkout pendente</h1>
          <p className="billingCheckoutStatus">{statusLabel[status] || status}</p>
        </div>

        <div className="billingCheckoutFields">
          <label htmlFor="paymentMethod">Metodo</label>
          <select
            id="paymentMethod"
            value={paymentMethod}
            onChange={event => setPaymentMethod(event.target.value)}
            disabled={status === "creating" || methods.length === 0}
          >
            {methods.length === 0 && <option value="PIX">PIX</option>}
            {methods.map(method => (
              <option key={method} value={method}>{method}</option>
            ))}
          </select>
        </div>

        {checkout?.redirectUrl && (
          <a className="billingCheckoutPrimary" href={checkout.redirectUrl} rel="noreferrer" target="_blank">
            Abrir checkout
          </a>
        )}

        {checkout?.pix?.copyPaste && (
          <button className="billingCheckoutSecondary" type="button" onClick={copyPix}>
            Copiar PIX
          </button>
        )}

        <div className="billingCheckoutActions">
          <button
            className="billingCheckoutPrimary"
            type="button"
            onClick={createCheckout}
            disabled={!featureState?.writeAvailable || !statusToken || status === "creating"}
          >
            {checkout && ["expired", "failed", "cancelled"].includes(checkout.status) ? "Regenerar" : "Criar checkout"}
          </button>
          <button
            className="billingCheckoutSecondary"
            type="button"
            onClick={cancelCheckout}
            disabled={!checkout?.checkoutId || status === "cancelled" || status === "expired"}
          >
            Cancelar
          </button>
        </div>

        {checkout?.expiresAt && <p className="billingCheckoutMuted">Expira em {new Date(checkout.expiresAt).toLocaleString()}</p>}
        {error && <p className="billingCheckoutError">{error}</p>}
      </section>
    </main>
  );
};

export default BillingCheckoutPendingPage;
